import React, { FC, useRef } from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import {
  ENTITY_TYPE,
  ReportingContextProvider,
  ReportAnIssueButton,
  LazyReportContentModal,
  PARENT_ENTITY_TYPE,
} from '@fiverr-private/content_reporting';
import { Stack } from '@fiverr-private/layout_components';
import {
  PortfolioModalSellerPortfolioProjectAttachmentFieldsFragment,
  PortfolioProjectModeration,
} from '../../../gql-operations/gql-generated';
import {
  formatPortfolioDescription,
  formatTitle,
  isInternalPortfolioProject,
  mapCategoryDetails,
  mapInternalProjectDetails,
} from '../../../utils';
import { AppI18n, buildSubCategoryTKey } from '../../../i18n';
import { AttachmentPreview } from '../../AttachmentPreview';
import { PortfolioModalProjectFullData } from '../../../server';
import { useBiPortfolioModalViewed } from '../PortfolioModal.bi';
import { usePortfolioModalContext } from '../PortfolioModalContext';
import { ModerationRemovedAlert } from '../ModerationRemovedAlert';
import { getPortfolioModerationDetails } from '../../../utils/getPortfolioModerationDetails/getPortfolioModerationDetails';
import { ModerationBadge } from '../ModerationBadge';
import { Body, BottomSection, BottomSectionItem, TopInfo } from './Layout';
import { ProjectHeader } from './Header/ProjectHeader';

export interface ProjectProps extends PortfolioModalProjectFullData {
  sellerId: string;
  moderation?: PortfolioProjectModeration;
}

export const Project: FC<ProjectProps> = ({
  id,
  title: initialTitle,
  source,
  description: initialDescription,
  projectStartedAt,
  price,
  duration,
  items,
  subCategoryIds,
  metadataTypes,
  services,
  industries,
  internalProjectDetails: initialInternalProjectDetails,
  sellerId,
  moderation,
}) => {
  const observableRef = useRef<HTMLDivElement>(null);

  const internalProjectDetails = mapInternalProjectDetails(initialInternalProjectDetails);
  const categoryDetails = mapCategoryDetails(subCategoryIds, internalProjectDetails?.nestedSubCategoryId);

  const isInternalProject = isInternalPortfolioProject(source, internalProjectDetails);

  const title = formatTitle(isInternalProject, initialTitle, categoryDetails);
  const description = formatPortfolioDescription(isInternalProject, initialDescription);
  const { isViolated, violatedFields, violationReason } = getPortfolioModerationDetails(moderation || null);

  const { inAttachmentReportRollout, isDisabledPortfolioProjectModeration } = usePortfolioModalContext();
  const isViolatedAndNotDisabledByRollout = isViolated && !isDisabledPortfolioProjectModeration;

  const { userId } = getContext();

  useBiPortfolioModalViewed(observableRef, { projectId: id, projectSource: source });

  const entityIds = items?.nodes?.map(({ attachment }) => attachment?.filerrAttachmentId) ?? [];

  return (
    <Body ref={observableRef}>
      <ReportingContextProvider
        entityId={entityIds as string[]}
        entityType={ENTITY_TYPE.ATTACHMENT}
        parentEntityType={PARENT_ENTITY_TYPE.PORTFOLIO_PROJECT}
        parentEntityId={id}
        buyerId={`${userId}`}
        sellerId={sellerId}
      >
        <LazyReportContentModal />
        {isViolatedAndNotDisabledByRollout && <ModerationRemovedAlert reason={violationReason} projectId={id} />}
        <ProjectHeader
          id={id}
          source={source}
          title={title}
          description={description}
          internalProjectDetails={internalProjectDetails}
          projectStartedAt={projectStartedAt}
          isViolated={isViolated}
          violatedFields={violatedFields}
        />
        <TopInfo duration={duration} price={price} industries={industries} />
        {items?.nodes?.map(
          ({ id, attachment }) =>
            isValidAttachment(attachment) && (
              <Stack direction="column" gap="2" key={id} position="relative">
                <AttachmentPreview
                  name={title ? `${title} attachment` : 'attachment'}
                  previewSrc={attachment.mediaUrl || attachment.previewUrl}
                  pdfSrc={attachment.pdfOpenUrl || undefined}
                  type={attachment.contentType}
                  width={attachment.metadata?.width || undefined}
                  height={attachment.metadata?.height || undefined}
                />
                {attachment.filerrAttachmentId &&
                  violatedFields?.violatedAttachmentIds?.includes(attachment.filerrAttachmentId) &&
                  isViolatedAndNotDisabledByRollout && <ModerationBadge position="absolute" top={16} left={16} />}
                {sellerId && attachment.filerrAttachmentId && inAttachmentReportRollout ? (
                  <Stack>
                    <ReportAnIssueButton
                      shouldTriggerAuth
                      shouldTriggerActivation
                      entityId={attachment.filerrAttachmentId}
                      variant="text"
                    />
                  </Stack>
                ) : null}
              </Stack>
            )
        )}
        {!!subCategoryIds.length && (
          <BottomSection title={<AppI18n k="project_modal.category" />}>
            <BottomSectionItem
              tags={subCategoryIds.map((id) => (
                <I18n key={id} k={buildSubCategoryTKey(id)} />
              ))}
            />
          </BottomSection>
        )}
        {!!services?.length && (
          <BottomSection title={<AppI18n k="project_modal.services" />}>
            <BottomSectionItem tags={services.map(({ name }) => name)} />
          </BottomSection>
        )}
        {!!metadataTypes?.length && (
          <BottomSection title={<AppI18n k="project_modal.metadata" />}>
            {metadataTypes.map(({ id, name, values }) => (
              <BottomSectionItem key={id} title={name} tags={values.map(({ name }) => name)} />
            ))}
          </BottomSection>
        )}
      </ReportingContextProvider>
    </Body>
  );
};

interface ValidAttachment {
  previewUrl: string;
  contentType: string;
}

const isValidAttachment = (
  attachment: PortfolioModalSellerPortfolioProjectAttachmentFieldsFragment | ValidAttachment | null
): attachment is ValidAttachment => !!(attachment?.previewUrl && attachment.contentType);
